import {skipToken} from '@reduxjs/toolkit/query';
import {useEventListener} from 'platform/components';

import {isNotNil} from 'ramda';

import {FilterValues, useGetGridTotalRowsQuery} from '@dms/api/core';

import {REFETCH_EAGRID_EVENT} from 'features/eag-grid';

import {useDataQuery} from './useDataQuery';

function isGridRefreshEvent(event: Event): event is CustomEvent<{gridCode: string}> {
  if (!(event instanceof CustomEvent)) {
    return false;
  }

  const detail: unknown = event.detail;
  return (
    isNotNil(detail) &&
    typeof detail === 'object' &&
    'gridCode' in detail &&
    typeof detail.gridCode === 'string'
  );
}

export function useDataGridTotalRows(
  dataGridCode: string,
  filters: FilterValues,
  options?: {skip?: boolean}
) {
  const {currentData: dataQuery} = useDataQuery(dataGridCode, {filters, skip: options?.skip});
  const dataQueryId = dataQuery?.dataQueryId;
  const totalRowsQueryArg = isNotNil(dataQueryId) ? {code: dataGridCode, dataQueryId} : skipToken;

  const {
    currentData: totalRows,
    refetch: refetchTotalRows,
    isUninitialized,
  } = useGetGridTotalRowsQuery(totalRowsQueryArg);

  const handleGridRefresh = (event: Event) => {
    if (
      !isGridRefreshEvent(event) ||
      event.detail.gridCode !== dataGridCode ||
      options?.skip ||
      isUninitialized
    ) {
      return;
    }

    void refetchTotalRows();
  };

  useEventListener<EventTarget, typeof REFETCH_EAGRID_EVENT>(
    window,
    REFETCH_EAGRID_EVENT,
    handleGridRefresh
  );

  return totalRows;
}
